"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_LAT, DEFAULT_LNG } from "./inspectionConstants";

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!;

let googleMapsPromise: Promise<void> | null = null;

function loadGoogleMapsScript(): Promise<void> {
    if (googleMapsPromise) return googleMapsPromise;
    if (typeof window !== "undefined" && window.google?.maps) {
        return (googleMapsPromise = Promise.resolve());
    }
    return (googleMapsPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Google Maps"));
        document.head.appendChild(script);
    }));
}

interface UseGoogleMapsOptions {
    /** Called whenever the pin position is resolved to a human address */
    onAddressResolved: (address: string, lat: number, lng: number) => void;
    /** Called when user selects an autocomplete suggestion */
    onPlaceSelected: (address: string, lat: number, lng: number) => void;
}

export function useGoogleMaps(
    mapContainerRef: React.RefObject<HTMLDivElement | null>,
    searchInputRef: React.RefObject<HTMLInputElement | null>,
    { onAddressResolved, onPlaceSelected }: UseGoogleMapsOptions,
) {
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const initialisedRef = useRef(false);

    // Keep callbacks in refs so map event listeners never go stale
    const onAddressResolvedRef = useRef(onAddressResolved);
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    useEffect(() => { onAddressResolvedRef.current = onAddressResolved; }, [onAddressResolved]);
    useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);

    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);

    // ── Reverse geocode ────────────────────────────────────────────────────────
    const reverseGeocode = useCallback((lat: number, lng: number) => {
        if (!window.google?.maps) return;
        new google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
            const address =
                status === "OK" && results?.[0]
                    ? results[0].formatted_address
                    : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
            onAddressResolvedRef.current(address, lat, lng);
        });
    }, []);

    // ── Move pin programmatically ─────────────────────────────────────────────
    const movePinTo = useCallback((lat: number, lng: number, zoom?: number, shouldResolveAddress = true) => {
        if (!mapRef.current || !markerRef.current) return;
        markerRef.current.setPosition({ lat, lng });
        mapRef.current.panTo({ lat, lng });
        if (zoom) mapRef.current.setZoom(zoom);
        if (shouldResolveAddress) reverseGeocode(lat, lng);
    }, [reverseGeocode]);

    // ── Load script ───────────────────────────────────────────────────────────
    useEffect(() => {
        loadGoogleMapsScript()
            .then(() => setIsLoaded(true))
            .catch(() => setLoadError(true));
    }, []);

    // ── Init map (runs exactly once after script loads) ────────────────────────
    useEffect(() => {
        if (!isLoaded || !mapContainerRef.current || initialisedRef.current) return;
        initialisedRef.current = true;

        const map = new google.maps.Map(mapContainerRef.current, {
            center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
            zoom: 12,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "cooperative",
            styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
        });

        const marker = new google.maps.Marker({
            position: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
            map,
            draggable: true,
            title: "Car location",
        });

        marker.addListener("dragend", () => {
            const pos = marker.getPosition();
            if (pos) reverseGeocode(pos.lat(), pos.lng());
        });

        map.addListener("click", (e: google.maps.MapMouseEvent) => {
            if (e.latLng) {
                marker.setPosition(e.latLng);
                reverseGeocode(e.latLng.lat(), e.latLng.lng());
            }
        });

        mapRef.current = map;
        markerRef.current = marker;
    }, [isLoaded, mapContainerRef, reverseGeocode]);

    // ── Init autocomplete (runs once map tab is active + script loaded) ────────
    useEffect(() => {
        if (!isLoaded || !searchInputRef.current || autocompleteRef.current) return;

        const ac = new google.maps.places.Autocomplete(searchInputRef.current, {
            componentRestrictions: { country: "ae" },
            fields: ["formatted_address", "geometry", "name"],
        });

        ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            if (!place.geometry?.location) return;
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const address = place.formatted_address || place.name || "";
            onPlaceSelectedRef.current(address, lat, lng);
            movePinTo(lat, lng, 16, false);
        });

        autocompleteRef.current = ac;
    }, [isLoaded, searchInputRef, movePinTo]);

    return { isLoaded, loadError, movePinTo };
}